/**
 * Reusable swatch-grid picker.
 *
 * Renders a flex-wrapped row of small square buttons with a custom swatch
 * renderer per item.  Used for matcap presets, viewport background presets,
 * and potentially environment map thumbnails in the future.
 */

import type { ReactNode } from "react";
import { COLOR_ACCENT, RADIUS_SM } from "./tokens.ts";

export interface SwatchGridProps<K extends string = string> {
  /** Ordered list of preset keys to display. */
  keys: readonly K[];
  /** Currently active key. */
  active: K;
  /** Called when the user clicks a swatch. */
  onSelect: (key: K) => void;
  /** Return the human-readable label for a key (used as tooltip). */
  label: (key: K) => string;
  /** Render the visual content for one swatch (sized to `size`). */
  renderSwatch: (key: K, size: number) => ReactNode;
  /** Swatch size in px. @default 32 */
  size?: number;
  /** Gap between swatches in px. @default 4 */
  gap?: number;
}

export function SwatchGrid<K extends string = string>({
  keys,
  active,
  onSelect,
  label,
  renderSwatch,
  size = 32,
  gap = 4,
}: SwatchGridProps<K>) {
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap, marginBottom: 8 }}>
      {keys.map((key) => {
        const isActive = key === active;
        return (
          <button
            key={key}
            title={label(key)}
            onClick={() => onSelect(key)}
            style={{
              width: size,
              height: size,
              borderRadius: RADIUS_SM,
              border: isActive
                ? `2px solid ${COLOR_ACCENT}`
                : "1px solid rgba(255,255,255,0.1)",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              overflow: "hidden",
              opacity: isActive ? 1 : 0.7,
            }}
          >
            {renderSwatch(key, size - (isActive ? 4 : 2))}
          </button>
        );
      })}
    </div>
  );
}
